import { useState, useEffect } from "react";
import { useServicesContent } from "@/hooks/useServicesContent";
import { servicesApi, settingsApi } from "@/integrations/mysql/client";
import { PageVisibilityToggle } from "@/components/ui/PageVisibilityToggle";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import {
  Loader2, Save, Trash2, ChevronDown, ChevronUp,
  Eye, EyeOff, Plus, X, Megaphone, LayoutGrid, ListChecks, Sparkles, GripVertical, TableProperties, Check,
  Heart, Brain, Leaf, Activity, Scale, Dumbbell, Baby,
  Stethoscope, Apple, Salad, Utensils, Coffee,
  Star, Shield, Target, Award, TrendingUp,
  Clock, Users, Building2, Handshake, CheckCircle, Zap, Globe,
  MessageCircle, Mail, MapPin, Phone, type LucideIcon,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ─── Badge icon helpers ────────────────────────────────────────────────────────
interface BadgeItem { label: string; icon: string; }

const BADGE_ICON_MAP: Record<string, LucideIcon> = {
  Users, Star, Sparkles, MessageCircle, Mail, MapPin, Phone, Globe,
  Heart, Award, Shield, Clock, CheckCircle, Zap, Target,
};

function parseBadges(raw: any[]): BadgeItem[] {
  return raw.map(b => typeof b === 'string' ? { label: b, icon: 'Star' } : b);
}

function BadgeIconPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const Icon = BADGE_ICON_MAP[value] || Star;
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
          open ? "border-brand-primary bg-brand-primary/8" : "border-gray-200 bg-white hover:border-brand-primary/50"
        }`}
        title={`Ícone: ${value}`}
      >
        <Icon className="w-4 h-4 text-brand-primary" />
      </button>
      {open && (
        <div className="absolute left-0 top-10 z-30 bg-white border border-gray-200 rounded-xl shadow-xl p-2 w-48">
          <div className="grid grid-cols-5 gap-1">
            {Object.entries(BADGE_ICON_MAP).map(([name, Ic]) => (
              <button
                key={name}
                type="button"
                title={name}
                onClick={() => { onChange(name); setOpen(false); }}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                  value === name
                    ? "bg-brand-primary text-white"
                    : "text-gray-500 hover:bg-brand-primary/10 hover:text-brand-primary"
                }`}
              >
                <Ic className="w-4 h-4" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Icon map ──────────────────────────────────────────────────────────────────
const SERVICE_ICON_MAP: Record<string, LucideIcon> = {
  Heart, Brain, Leaf, Activity, Scale, Dumbbell, Baby,
  Stethoscope, Apple, Salad, Utensils, Coffee,
  Star, Shield, Sparkles, Target, Award, TrendingUp,
  Clock, Users, Building2, Handshake, CheckCircle, Zap, Globe,
};

// ─── Icon Picker ───────────────────────────────────────────────────────────────
function IconPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const SelectedIcon = SERVICE_ICON_MAP[value] || Heart;

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white hover:border-brand-primary/50 transition-colors w-full text-left"
      >
        <SelectedIcon className="w-4 h-4 text-brand-primary shrink-0" />
        <span className="flex-1">{value || "Selecionar ícone"}</span>
        <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
      </button>
      {open && (
        <div className="border border-gray-200 rounded-xl p-3 bg-white shadow-sm">
          <div className="grid grid-cols-5 gap-1.5">
            {Object.entries(SERVICE_ICON_MAP).map(([name, Icon]) => (
              <button
                key={name}
                type="button"
                onClick={() => { onChange(name); setOpen(false); }}
                title={name}
                className={`flex flex-col items-center gap-1 px-1 py-2 rounded-lg transition-all text-center ${
                  value === name
                    ? "bg-brand-primary text-white"
                    : "hover:bg-brand-primary/8 text-gray-600 hover:text-brand-primary"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className="text-[9px] font-medium leading-tight truncate w-full">{name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── small helpers ────────────────────────────────────────────────────────────

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</label>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, maxLength }: {
  value: string; onChange: (v: string) => void; placeholder?: string; maxLength?: number;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
    />
  );
}

function TextArea({ value, onChange, placeholder, rows = 3, maxLength }: {
  value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; maxLength?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all resize-none"
    />
  );
}

function SaveBar({ dirty, saving, onSave }: { dirty: boolean; saving: boolean; onSave: () => void }) {
  if (!dirty) return null;
  return (
    <div className="flex items-center justify-between bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3">
      <p className="text-xs text-brand-primary font-medium">Você tem alterações não salvas</p>
      <button
        onClick={onSave}
        disabled={saving}
        className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        Salvar
      </button>
    </div>
  );
}

// ─── Feature tag editor ────────────────────────────────────────────────────
function FeatureEditor({ features, onChange }: { features: string[]; onChange: (f: string[]) => void }) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const trimmed = draft.trim();
    if (trimmed && !features.includes(trimmed)) {
      onChange([...features, trimmed]);
    }
    setDraft("");
  };

  const remove = (i: number) => onChange(features.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {/* Tag list */}
      <div className="flex flex-wrap gap-1.5 min-h-[32px]">
        {features.map((f, i) => (
          <span key={i} className="flex items-center gap-1 bg-brand-primary/8 text-brand-primary text-xs font-medium px-2.5 py-1 rounded-lg">
            {f}
            <button onClick={() => remove(i)} className="text-brand-primary/50 hover:text-brand-primary transition-colors ml-0.5">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {features.length === 0 && (
          <span className="text-xs text-gray-400 italic">Nenhum diferencial adicionado</span>
        )}
      </div>
      {/* Input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Digite e pressione Enter para adicionar..."
          className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
        />
        <button
          onClick={add}
          className="flex items-center gap-1 bg-gray-100 hover:bg-brand-primary/10 text-gray-600 hover:text-brand-primary text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Adicionar
        </button>
      </div>
    </div>
  );
}

// ─── Status pill ───────────────────────────────────────────────────────────
function StatusToggle({ active, onChange }: { active: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
        active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
      }`}
    >
      {active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
      {active ? "Visível" : "Oculto"}
    </button>
  );
}

// ─── Collapsible service card ──────────────────────────────────────────────
function ServiceCard({
  service,
  onSave,
  onDelete,
  showFeatures = true,
  dragHandleProps,
}: {
  service: any;
  onSave: (id: string, data: any) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  showFeatures?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}) {
  const original = service.content_value as any;
  const [form, setForm] = useState({ ...original, is_active: service.is_active });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const dirty =
    form.title !== original.title ||
    form.description !== original.description ||
    form.buttonText !== original.buttonText ||
    form.buttonUrl !== original.buttonUrl ||
    form.icon !== original.icon ||
    form.highlighted !== original.highlighted ||
    JSON.stringify(form.features) !== JSON.stringify(original.features) ||
    form.is_active !== service.is_active;

  const update = (key: string, val: any) => setForm((p: any) => ({ ...p, [key]: val }));

  const save = async () => {
    setSaving(true);
    const { is_active, ...contentValue } = form;
    await onSave(service.id, { content_value: contentValue, is_active });
    setSaving(false);
  };

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${open ? "border-brand-primary/30 shadow-sm" : "border-gray-100"}`}>
      {/* Card header — always visible */}
      <div className="flex items-center justify-between px-4 py-3 bg-white">
        <div className="flex items-center gap-3 min-w-0">
          {dragHandleProps && (
            <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 shrink-0 touch-none">
              <GripVertical className="w-4 h-4" />
            </div>
          )}
          <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-left min-w-0 group">
            {open
              ? <ChevronUp className="w-4 h-4 text-brand-primary shrink-0" />
              : <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600 shrink-0" />}
            <span className="font-semibold text-sm text-gray-800 truncate group-hover:text-brand-primary transition-colors">
              {form.title || <span className="text-gray-400 italic">Sem título</span>}
            </span>
          </button>
          {dirty && <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400" title="Alterações não salvas" />}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <StatusToggle
            active={form.is_active}
            onChange={() => {
              const next = !form.is_active;
              update("is_active", next);
              // Auto-save visibility toggle immediately
              onSave(service.id, { is_active: next });
            }}
          />
          {onDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir serviço</AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza? Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(service.id)} className="bg-red-600 hover:bg-red-700">
                    Excluir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Expanded fields */}
      {open && (
        <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4 space-y-4">
          <Field label="Nome do serviço" hint="Aparece no cabeçalho verde do card">
            <TextInput value={form.title || ""} onChange={(v) => update("title", v)} placeholder="Ex: Acompanhamento Nutricional" maxLength={100} />
          </Field>

          <Field label="Descrição" hint="Texto explicativo abaixo do título">
            <TextArea value={form.description || ""} onChange={(v) => update("description", v)} placeholder="Descreva brevemente este serviço..." rows={3} maxLength={400} />
          </Field>

          {showFeatures && (
            <Field label="Diferenciais incluídos" hint="O que o paciente recebe neste serviço">
              <FeatureEditor
                features={form.features || []}
                onChange={(f) => update("features", f)}
              />
            </Field>
          )}

          <Field label="Ícone do serviço" hint="Ícone exibido no cabeçalho do card">
            <IconPicker value={form.icon || "Heart"} onChange={(v) => update("icon", v)} />
          </Field>

          <Field label="Texto do botão de agendamento">
            <TextInput value={form.buttonText || ""} onChange={(v) => update("buttonText", v)} placeholder="Ex: Agendar Consulta" maxLength={50} />
          </Field>

          <Field label="URL do botão (opcional)" hint="Se preenchido, o botão abre este link em vez do agendamento padrão">
            <TextInput value={form.buttonUrl || ""} onChange={(v) => update("buttonUrl", v)} placeholder="Ex: https://wa.me/55..." maxLength={500} />
          </Field>

          <div className="flex items-center justify-between px-3 py-2.5 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div>
              <p className="text-xs font-semibold text-gray-700">Destaque ⭐</p>
              <p className="text-xs text-gray-500">Exibe badge "Destaque" no card</p>
            </div>
            <Switch checked={!!form.highlighted} onCheckedChange={(v) => update("highlighted", v)} />
          </div>

          <SaveBar dirty={dirty} saving={saving} onSave={save} />
        </div>
      )}
    </div>
  );
}

// ─── Sortable service card wrapper ─────────────────────────────────────────
function SortableServiceCard({
  service, onSave, onDelete, showFeatures,
}: {
  service: any;
  onSave: (id: string, data: any) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  showFeatures?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: service.id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}>
      <ServiceCard
        service={service}
        onSave={onSave}
        onDelete={onDelete}
        showFeatures={showFeatures}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

// ─── Section wrapper ───────────────────────────────────────────────────────
function Section({ icon: Icon, title, description, children }: {
  icon: React.ElementType; title: string; description: string; children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
        <div className="w-8 h-8 rounded-lg bg-brand-primary/8 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-brand-primary" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-800">{title}</h3>
          <p className="text-xs text-gray-400">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

// ─── Default comparison data ───────────────────────────────────────────────
const DEFAULT_COMPARISONS = [
  {
    title: "Acompanhamento Nutricional",
    description: "Plano personalizado para reeducação alimentar e mudança de hábitos",
    services: [
      { name: "Consulta inicial completa (90 min)", included: true },
      { name: "Anamnese alimentar detalhada", included: true },
      { name: "Avaliação antropométrica", included: true },
      { name: "Plano alimentar personalizado", included: true },
      { name: "Lista de substituições", included: true },
      { name: "Retorno em 30 dias", included: true },
      { name: "Suporte via WhatsApp", included: true },
      { name: "Receitas exclusivas", included: false },
      { name: "Acompanhamento semanal", included: false },
    ],
  },
  {
    title: "Personal Diet",
    description: "Acompanhamento intensivo com suporte contínuo",
    services: [
      { name: "Tudo do Acompanhamento Nutricional", included: true },
      { name: "Consultas semanais", included: true },
      { name: "Suporte diário via WhatsApp", included: true },
      { name: "Cardápio semanal detalhado", included: true },
      { name: "Receitas exclusivas", included: true },
      { name: "Orientação para compras", included: true },
      { name: "Ajustes em tempo real", included: true },
      { name: "Monitoramento diário", included: true },
      { name: "Relatórios de progresso", included: true },
    ],
  },
  {
    title: "Nutrição Esportiva",
    description: "Especializado para atletas e praticantes de atividade física",
    services: [
      { name: "Avaliação da composição corporal", included: true },
      { name: "Plano alimentar para treino", included: true },
      { name: "Estratégia de hidratação", included: true },
      { name: "Suplementação orientada", included: true },
      { name: "Periodização nutricional", included: true },
      { name: "Acompanhamento de performance", included: true },
      { name: "Orientação pré e pós-treino", included: true },
      { name: "Plano para competições", included: false },
      { name: "Acompanhamento presencial", included: false },
    ],
  },
  {
    title: "Reeducação Alimentar",
    description: "Foco na mudança gradual e sustentável de hábitos",
    services: [
      { name: "Identificação de padrões alimentares", included: true },
      { name: "Educação nutricional", included: true },
      { name: "Estratégias comportamentais", included: true },
      { name: "Plano de transição gradual", included: true },
      { name: "Material educativo", included: true },
      { name: "Acompanhamento psicológico", included: false },
      { name: "Grupos de apoio", included: false },
      { name: "Oficinas culinárias", included: false },
      { name: "Suporte familiar", included: false },
    ],
  },
];

// ─── Comparison editor ─────────────────────────────────────────────────────
function ComparisonEditor() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [tables, setTables] = useState(DEFAULT_COMPARISONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEnabled, setShowEnabled] = useState(false);
  const [showSaving, setShowSaving] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  useEffect(() => {
    (async () => {
      const settings = await settingsApi.list();
      const raw = settings.find((s: any) => s.key === "service_comparisons")?.value;
      if (raw) { try { setTables(JSON.parse(raw)); } catch {} }
      const show = settings.find((s: any) => s.key === "show_comparison_tables")?.value;
      setShowEnabled(show === "true");
      setLoading(false);
    })();
  }, []);

  const handleToggleShow = async (v: boolean) => {
    setShowEnabled(v);
    setShowSaving(true);
    await settingsApi.update("show_comparison_tables", v ? "true" : "false");
    queryClient.invalidateQueries({ queryKey: ["site-settings"] });
    setShowSaving(false);
    toast({ title: v ? "Tabela ativada no site!" : "Tabela ocultada do site." });
  };

  const handleSave = async () => {
    setSaving(true);
    await settingsApi.update("service_comparisons", JSON.stringify(tables));
    queryClient.invalidateQueries({ queryKey: ["site-settings"] });
    setSaving(false);
    toast({ title: "Tabela de comparação salva!" });
  };

  const updateTable = (i: number, key: string, val: string) =>
    setTables(prev => prev.map((t, idx) => idx === i ? { ...t, [key]: val } : t));

  const updateItem = (ti: number, ii: number, key: string, val: any) =>
    setTables(prev => prev.map((t, tidx) => tidx !== ti ? t : {
      ...t,
      services: t.services.map((s, sidx) => sidx === ii ? { ...s, [key]: val } : s),
    }));

  const addItem = (ti: number) =>
    setTables(prev => prev.map((t, idx) => idx !== ti ? t : {
      ...t, services: [...t.services, { name: "", included: true }],
    }));

  const removeItem = (ti: number, ii: number) =>
    setTables(prev => prev.map((t, idx) => idx !== ti ? t : {
      ...t, services: t.services.filter((_, sidx) => sidx !== ii),
    }));

  const addTable = () => {
    setTables(prev => [...prev, { title: "Novo plano", description: "", services: [] }]);
    setExpandedIndex(tables.length);
  };

  const removeTable = (i: number) => {
    setTables(prev => prev.filter((_, idx) => idx !== i));
    setExpandedIndex(null);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;

  return (
    <Section icon={TableProperties} title="Tabela de Comparação" description="Gerencie os planos comparativos exibidos na página de serviços">
      {/* Toggle exibir no site */}
      <div className={`flex items-center justify-between px-4 py-3 rounded-xl border mb-4 ${showEnabled ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`}>
        <div>
          <p className="text-sm font-semibold text-gray-800">Exibir tabela no site</p>
          <p className={`text-xs ${showEnabled ? "text-emerald-600" : "text-gray-400"}`}>
            {showEnabled ? "Visível na página de serviços" : "Oculta da página de serviços"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {showSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
          <Switch checked={showEnabled} onCheckedChange={handleToggleShow} />
        </div>
      </div>

      {/* Tables list */}
      <div className="space-y-3">
        {tables.map((table, ti) => (
          <div key={ti} className="border border-gray-200 rounded-xl overflow-hidden">
            {/* Table header */}
            <div
              className="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setExpandedIndex(expandedIndex === ti ? null : ti)}
            >
              <span className="w-5 h-5 rounded-full bg-brand-primary/10 text-brand-primary text-[10px] font-bold flex items-center justify-center shrink-0">{ti + 1}</span>
              <span className="flex-1 text-sm font-semibold text-gray-800 truncate">{table.title || "Sem título"}</span>
              <span className="text-xs text-gray-400">{table.services.length} itens</span>
              {expandedIndex === ti ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
            </div>

            {expandedIndex === ti && (
              <div className="p-4 space-y-4 bg-white">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Título do plano">
                    <TextInput value={table.title} onChange={v => updateTable(ti, "title", v)} placeholder="Ex: Acompanhamento Nutricional" maxLength={100} />
                  </Field>
                  <Field label="Descrição curta">
                    <TextInput value={table.description} onChange={v => updateTable(ti, "description", v)} placeholder="Ex: Plano personalizado..." maxLength={200} />
                  </Field>
                </div>

                {/* Items */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Itens do plano</p>
                  <div className="space-y-2">
                    {table.services.map((item, ii) => (
                      <div key={ii} className={`flex items-center gap-2 px-1.5 rounded-lg transition-colors ${(item as any).highlight ? "bg-brand-primary/5 border border-brand-primary/20" : "border border-transparent"}`}>
                        <button
                          onClick={() => updateItem(ti, ii, "included", !item.included)}
                          title={item.included ? "Marcar como não incluso" : "Marcar como incluso"}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold shrink-0 transition-all ${
                            item.included
                              ? "bg-brand-primary border-brand-primary text-white"
                              : "border-gray-300 bg-white text-gray-400"
                          }`}
                        >
                          {item.included
                            ? <><Check className="w-3 h-3" strokeWidth={3} /> Incluso</>
                            : <><X className="w-3 h-3" /> Não</>}
                        </button>
                        <button
                          onClick={async () => {
                            const newHighlight = !(item as any).highlight;
                            const newTables = tables.map((t2, tidx) => tidx !== ti ? t2 : {
                              ...t2,
                              services: t2.services.map((s, sidx) => sidx === ii ? { ...s, highlight: newHighlight } : s),
                            });
                            setTables(newTables);
                            await settingsApi.update("service_comparisons", JSON.stringify(newTables));
                          }}
                          title={(item as any).highlight ? "Remover cor de destaque da linha" : "Destacar esta linha com cor diferenciada"}
                          className={`shrink-0 text-base leading-none transition-all ${(item as any).highlight ? "text-amber-400" : "text-gray-200 hover:text-amber-300"}`}
                        >★</button>
                        <input
                          type="text"
                          value={item.name}
                          onChange={e => updateItem(ti, ii, "name", e.target.value)}
                          placeholder="Nome do item"
                          maxLength={120}
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                        />
                        <button onClick={() => removeItem(ti, ii)} className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => addItem(ti)} className="mt-3 flex items-center gap-1.5 text-xs text-brand-primary font-semibold hover:underline">
                    <Plus className="w-3.5 h-3.5" /> Adicionar item
                  </button>
                </div>

                <div className="flex justify-end pt-1">
                  <button onClick={() => removeTable(ti)} className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 font-medium">
                    <Trash2 className="w-3.5 h-3.5" /> Remover este plano
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        <button onClick={addTable} className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-3 text-sm text-gray-400 hover:border-brand-primary/40 hover:text-brand-primary transition-colors">
          <Plus className="w-4 h-4" /> Adicionar plano
        </button>
      </div>

      {/* Save */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-semibold px-5 py-2 rounded-xl transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar tabela
        </button>
      </div>
    </Section>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export function ServicesManager() {
  const { content, loading, updateContent, createContent, deleteContent } = useServicesContent();
  const [activeTab, setActiveTab] = useState<"principais" | "adicionais" | "cabecalho" | "cta" | "comparacao">("cabecalho");
  const [localMain, setLocalMain] = useState<any[] | null>(null);
  const [localAdditional, setLocalAdditional] = useState<any[] | null>(null);
  const [creatingMain, setCreatingMain] = useState(false);
  const [creatingAdditional, setCreatingAdditional] = useState(false);
  const { toast } = useToast();

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  // Hero local state
  const heroContent = content.find((c) => c.section === "hero");
  const [heroForm, setHeroForm] = useState<any>({});
  const [heroSaving, setHeroSaving] = useState(false);
  useEffect(() => {
    if (heroContent) setHeroForm({
      label: "Atendimento Personalizado",
      title: "Nossos Serviços",
      subtitle: "Soluções personalizadas para cada necessidade, sempre com foco no seu bem-estar",
      badges: [{ label: "+500 Pacientes", icon: "Users" }, { label: "5.0 no Google", icon: "Star" }, { label: "Plano Personalizado", icon: "Sparkles" }],
      ...(heroContent.content_value as any),
    });
  }, [heroContent?.id]);
  const heroDirty = heroContent &&
    (heroForm.title !== (heroContent.content_value as any).title ||
     heroForm.subtitle !== (heroContent.content_value as any).subtitle ||
     heroForm.label !== (heroContent.content_value as any).label ||
     JSON.stringify(heroForm.badges) !== JSON.stringify((heroContent.content_value as any).badges));

  // CTA local state
  const ctaContent = content.find((c) => c.section === "cta");
  const [ctaForm, setCtaForm] = useState<any>({});
  const [ctaSaving, setCtaSaving] = useState(false);
  useEffect(() => {
    if (ctaContent) setCtaForm({
      title: "Pronto para Começar sua Jornada?",
      subtitle: "Escolha o serviço ideal para você e agende sua consulta",
      buttonText: "Agendar Agora",
      buttonUrl: "https://calendar.app.google/ULCXQLbU7iFtuVHh7",
      ...(ctaContent.content_value as any),
    });
  }, [ctaContent?.id]);
  const ctaDirty = ctaContent &&
    (ctaForm.title !== (ctaContent.content_value as any).title ||
     ctaForm.subtitle !== (ctaContent.content_value as any).subtitle ||
     ctaForm.buttonText !== (ctaContent.content_value as any).buttonText ||
     ctaForm.buttonUrl !== (ctaContent.content_value as any).buttonUrl);

  const mainServices = localMain ?? content.filter((c) => c.section === "main_services").sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  const additionalServices = localAdditional ?? content.filter((c) => c.section === "additional_services").sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  const handleDragEndMain = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const reordered = arrayMove(mainServices, mainServices.findIndex(s => s.id === active.id), mainServices.findIndex(s => s.id === over.id));
    setLocalMain(reordered);
    try {
      await Promise.all(reordered.map((s, i) => servicesApi.update(s.id, { display_order: i })));
      toast({ title: "Ordem atualizada!" });
    } catch {
      toast({ title: "Erro ao atualizar ordem", variant: "destructive" });
      setLocalMain(null);
    }
  };

  const handleDragEndAdditional = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const reordered = arrayMove(additionalServices, additionalServices.findIndex(s => s.id === active.id), additionalServices.findIndex(s => s.id === over.id));
    setLocalAdditional(reordered);
    try {
      await Promise.all(reordered.map((s, i) => servicesApi.update(s.id, { display_order: i })));
      toast({ title: "Ordem atualizada!" });
    } catch {
      toast({ title: "Erro ao atualizar ordem", variant: "destructive" });
      setLocalAdditional(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
        <p className="text-sm text-gray-400">Carregando conteúdo...</p>
      </div>
    );
  }

  const tabs = [
    { id: "cabecalho",   label: "Cabeçalho da Página",  icon: Megaphone },
    { id: "principais",  label: "Serviços Principais",  icon: LayoutGrid },
    { id: "adicionais",  label: "Serviços Adicionais",  icon: ListChecks },
    { id: "cta",         label: "Chamada para Ação",    icon: Sparkles },
    { id: "comparacao",  label: "Tabela de Comparação", icon: TableProperties },
  ] as const;

  return (
    <div className="space-y-6">
      <PageVisibilityToggle pageKey="page_servicos_visible" label="Página de Serviços" />

      {/* Tab navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
              activeTab === tab.id
                ? "bg-white text-brand-primary shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <tab.icon className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── Serviços Principais ─────────────────────────────────────────── */}
      {activeTab === "principais" && (
        <Section
          icon={LayoutGrid}
          title="Serviços Principais"
          description={`${mainServices.filter(s => s.is_active).length} de ${mainServices.length} visíveis no site`}
        >
          {mainServices.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-gray-400 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5" />Arraste para reordenar</p>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndMain}>
                <SortableContext items={mainServices.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  {mainServices.map((service) => (
                    <SortableServiceCard
                      key={service.id}
                      service={service}
                      onSave={async (id, data) => { await updateContent(id, data); }}
                      onDelete={async (id) => { await deleteContent(id); }}
                      showFeatures={true}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}
          {mainServices.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">Nenhum serviço principal cadastrado.</p>
          )}
          <button
            disabled={creatingMain}
            onClick={async () => {
              setCreatingMain(true);
              await createContent({
                section: "main_services",
                content_key: `service_${Date.now()}`,
                content_value: { title: "Novo Serviço", description: "", features: [], buttonText: "Agendar Consulta", icon: "Heart", buttonUrl: "", highlighted: false },
                display_order: mainServices.length,
                is_active: true,
              });
              setCreatingMain(false);
            }}
            className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-3 text-sm text-gray-400 hover:border-brand-primary/40 hover:text-brand-primary transition-colors disabled:opacity-60"
          >
            {creatingMain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Adicionar serviço principal
          </button>
        </Section>
      )}

      {/* ── Serviços Adicionais ─────────────────────────────────────────── */}
      {activeTab === "adicionais" && (
        <Section
          icon={ListChecks}
          title="Serviços Adicionais"
          description={`${additionalServices.filter(s => s.is_active).length} de ${additionalServices.length} visíveis no site`}
        >
          {additionalServices.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-gray-400 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5" />Arraste para reordenar</p>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndAdditional}>
                <SortableContext items={additionalServices.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  {additionalServices.map((service) => (
                    <SortableServiceCard
                      key={service.id}
                      service={service}
                      onSave={async (id, data) => { await updateContent(id, data); }}
                      onDelete={async (id) => { await deleteContent(id); }}
                      showFeatures={false}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}
          {additionalServices.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">Nenhum serviço adicional cadastrado.</p>
          )}
          <button
            disabled={creatingAdditional}
            onClick={async () => {
              setCreatingAdditional(true);
              await createContent({
                section: "additional_services",
                content_key: `service_add_${Date.now()}`,
                content_value: { title: "Novo Serviço", description: "", features: [], buttonText: "Agendar Consulta", icon: "Heart", buttonUrl: "", highlighted: false },
                display_order: additionalServices.length,
                is_active: true,
              });
              setCreatingAdditional(false);
            }}
            className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-3 text-sm text-gray-400 hover:border-brand-primary/40 hover:text-brand-primary transition-colors disabled:opacity-60"
          >
            {creatingAdditional ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Adicionar serviço adicional
          </button>
        </Section>
      )}

      {/* ── Cabeçalho da Página ─────────────────────────────────────────── */}
      {activeTab === "cabecalho" && heroContent && (
        <Section
          icon={Megaphone}
          title="Cabeçalho da Página de Serviços"
          description="Texto exibido no topo da página quando o visitante acessa Serviços"
        >
          <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500">Visibilidade do cabeçalho</p>
              <StatusToggle
                active={heroContent.is_active}
                onChange={() => updateContent(heroContent.id, { is_active: !heroContent.is_active })}
              />
            </div>
            <Field label="Chip / etiqueta" hint="Texto pequeno acima do título (ex: Atendimento Personalizado)">
              <TextInput
                value={heroForm.label || ""}
                onChange={(v) => setHeroForm((p: any) => ({ ...p, label: v }))}
                placeholder="Ex: Atendimento Personalizado"
                maxLength={80}
              />
            </Field>
            <Field label="Título principal" hint="Texto grande em destaque">
              <TextInput
                value={heroForm.title || ""}
                onChange={(v) => setHeroForm((p: any) => ({ ...p, title: v }))}
                placeholder="Ex: Nossos Serviços"
                maxLength={200}
              />
            </Field>
            <Field label="Subtítulo" hint="Frase de apoio abaixo do título">
              <TextArea
                value={heroForm.subtitle || ""}
                onChange={(v) => setHeroForm((p: any) => ({ ...p, subtitle: v }))}
                placeholder="Descreva brevemente os serviços oferecidos..."
                rows={3}
                maxLength={500}
              />
            </Field>
            <Field label="Badges / destaques" hint="Itens exibidos na faixa inferior do cabeçalho">
              <div className="space-y-2">
                {parseBadges(heroForm.badges || []).map((badge: BadgeItem, i: number) => (
                  <div key={i} className="flex items-center gap-2">
                    <BadgeIconPicker
                      value={badge.icon}
                      onChange={(v) => {
                        const updated = parseBadges([...(heroForm.badges || [])]);
                        updated[i] = { ...updated[i], icon: v };
                        setHeroForm((p: any) => ({ ...p, badges: updated }));
                      }}
                    />
                    <input
                      type="text"
                      value={badge.label}
                      onChange={(e) => {
                        const updated = parseBadges([...(heroForm.badges || [])]);
                        updated[i] = { ...updated[i], label: e.target.value };
                        setHeroForm((p: any) => ({ ...p, badges: updated }));
                      }}
                      placeholder="Ex: +500 Pacientes"
                      maxLength={40}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
                    />
                    <button
                      onClick={() => {
                        const updated = parseBadges(heroForm.badges || []).filter((_: BadgeItem, idx: number) => idx !== i);
                        setHeroForm((p: any) => ({ ...p, badges: updated }));
                      }}
                      className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setHeroForm((p: any) => ({ ...p, badges: [...parseBadges(p.badges || []), { label: "", icon: "Star" }] }))}
                  className="flex items-center gap-1.5 text-xs text-brand-primary font-semibold hover:underline"
                >
                  <Plus className="w-3.5 h-3.5" /> Adicionar badge
                </button>
              </div>
            </Field>
            <SaveBar
              dirty={!!heroDirty}
              saving={heroSaving}
              onSave={async () => {
                setHeroSaving(true);
                await updateContent(heroContent.id, { content_value: heroForm });
                setHeroSaving(false);
              }}
            />
          </div>
        </Section>
      )}

      {/* ── Tabela de Comparação ────────────────────────────────────────── */}
      {activeTab === "comparacao" && (
        <ComparisonEditor />
      )}

      {/* ── Chamada para Ação ───────────────────────────────────────────── */}
      {activeTab === "cta" && ctaContent && (
        <Section
          icon={Sparkles}
          title="Chamada para Ação"
          description="Bloco verde no final da página que incentiva o agendamento"
        >
          <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500">Exibir bloco no site</p>
              <StatusToggle
                active={ctaContent.is_active}
                onChange={() => updateContent(ctaContent.id, { is_active: !ctaContent.is_active })}
              />
            </div>
            <Field label="Título" hint="Frase principal de impacto">
              <TextInput
                value={ctaForm.title || ""}
                onChange={(v) => setCtaForm((p: any) => ({ ...p, title: v }))}
                placeholder="Ex: Pronto para cuidar da sua saúde?"
                maxLength={200}
              />
            </Field>
            <Field label="Subtítulo" hint="Complemento da frase principal">
              <TextArea
                value={ctaForm.subtitle || ""}
                onChange={(v) => setCtaForm((p: any) => ({ ...p, subtitle: v }))}
                placeholder="Ex: Agende sua consulta e dê o primeiro passo..."
                rows={2}
                maxLength={300}
              />
            </Field>
            <Field label="Texto do botão" hint="Botão de agendamento">
              <TextInput
                value={ctaForm.buttonText || ""}
                onChange={(v) => setCtaForm((p: any) => ({ ...p, buttonText: v }))}
                placeholder="Ex: Agendar Agora"
                maxLength={50}
              />
            </Field>
            <Field label="URL do botão (opcional)" hint="Se preenchido, o botão abre este link. Deixe vazio para usar o Google Calendar padrão">
              <TextInput
                value={ctaForm.buttonUrl || ""}
                onChange={(v) => setCtaForm((p: any) => ({ ...p, buttonUrl: v }))}
                placeholder="Ex: https://wa.me/55... ou https://calendly.com/..."
                maxLength={500}
              />
            </Field>
            <SaveBar
              dirty={!!ctaDirty}
              saving={ctaSaving}
              onSave={async () => {
                setCtaSaving(true);
                await updateContent(ctaContent.id, { content_value: ctaForm });
                setCtaSaving(false);
              }}
            />
          </div>
        </Section>
      )}
    </div>
  );
}
